import React, {Fragment} from 'react';

import cs from 'classnames';
import {colors} from 'js-libs/sweetcommons/styles';
import styled, {keyframes} from 'styled-components';

import {UserAvatar} from 'app/components/general/avatars';
import ElapsedTime from 'app/components/general/ElapsedTime';
import {StyledWithMentionsDiv} from 'app/components/general/Mentionable';
import {ActionsButton} from 'app/general/widgets/ActionsButton';
import {AddReactionButton} from 'app/reactions/components/AddReactionButton';
import {ReactionsCounterButtons} from 'app/reactions/components/ReactionsCounterButtons';
import {filterOutEmptyReactions} from 'app/reactions/utils';
import {gt} from 'app/utils/i18n';
import {markAsSafe} from 'app/utils/strings';

import {CommentSerializedType} from 'sweet-types';

interface Props {
  isTarget: boolean;
  comment: CommentSerializedType;
  canDelete: boolean;
  onDelete?: (url: string) => Promise<any>;
}

export function CommentItem({
  isTarget,
  comment,
  canDelete,
  onDelete,
}: Props): JSX.Element {
  const deleteComment = () => {
    if (onDelete != null) {
      return onDelete(comment.url);
    }
  };

  const {user, id, submit_date, is_removed, reactions} = comment;

  const hasReactions =
    Object.keys(filterOutEmptyReactions(reactions)).length > 0;

  return (
    <StyledHighlightedLI
      id={`comment${id}`}
      className={cs('p-2 pb-1 border-bottom', {
        deleted: is_removed,
        target: isTarget,
      })}
    >
      {is_removed ? (
        <div className="media">
          <div style={{width: '2.2em'}} className="mr-2" />
          <div className="media-body">
            <StyledDeletedComment className="small d-flex">
              <i className="fa fa-ban" /> {gt('Comment deleted')}
            </StyledDeletedComment>
          </div>
        </div>
      ) : (
        <Fragment>
          <div className="media">
            <UserAvatar className="mr-2" user={user} showCard />
            <div className="media-body">
              <div className="user-name d-flex justify-content-between font-weight-≤bold mb-1">
                {user != null ? user.name : null}
                {canDelete && comment.is_removed === false ? (
                  <ActionsButton
                    actions={[
                      {
                        text: gt('Delete comment'),
                        icon: 'far fa-trash',
                        key: 'delete',
                        callback: deleteComment,
                      },
                    ]}
                  >
                    {(buttonProps) => (
                      <button
                        {...buttonProps}
                        className="btn btn-link btn-sm options-menu py-0 px-1 text-muted"
                      />
                    )}
                  </ActionsButton>
                ) : null}
              </div>
              <StyledWithMentionsDiv
                className="message small"
                dangerouslySetInnerHTML={markAsSafe(comment.comment)}
              />
              <div className="d-flex align-items-center  pt-2">
                <small className="text-muted font-italic flex-grow-1">
                  <ElapsedTime
                    timestamp={submit_date}
                    tooltipPlacement="top"
                    ContainerTag="span"
                    small={true}
                  />
                </small>
                {!hasReactions ? (
                  <AddReactionButton
                    reactions={reactions}
                    comment={comment}
                    placement="left"
                  />
                ) : null}
              </div>
            </div>
          </div>
          {hasReactions ? (
            <div className="mt-1 d-flex reactions justify-content-between">
              <ReactionsCounterButtons
                comment={comment}
                reactions={reactions}
              />
              <AddReactionButton reactions={reactions} comment={comment} />
            </div>
          ) : null}
        </Fragment>
      )}
    </StyledHighlightedLI>
  );
}

const StyledDeletedComment = styled.div`
  color: ${colors.text};
  i {
    margin-top: 4px;
    margin-right: 3px;
  }
`;
const fadeInKeyframe = keyframes`
    0% {
        opacity: 0;
        transform: translateX(-10px);
    }
    100% {
        opacity: 1;
        transform: translateX(-0px);
    }
`;
const bounceHighlightKeyframe = keyframes`
    0% {background-color: transparent;}
    50% {background-color: #FFA;}
    100% {background-color: transparent;}
`;
const StyledHighlightedLI = styled.li`
  &.target {
    animation: ${bounceHighlightKeyframe} 2s, ${fadeInKeyframe} 500ms;
  }
  &.deleted {
    opacity: 0.7;
  }
  transition: opacity 250ms;

  .media-body {
    overflow: auto;
  }

  .user-name {
    font-size: 80%;

    .options-menu {
      position: relative;
      top: -3px;
    }
  }

  .message {
    word-wrap: break-word;
    word-break: break-word;

    a {
      word-wrap: anywhere;
    }
  }
`;
